/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { useTranslate } from "@adapptio/lib-components";
import { IInspectorNode_Default } from "../../InspectorNodeTypes";
import { InspectorControl } from "../../controls";
import {
	FieldLabel,
	FieldRow,
	FieldSection,
	FieldSectionContent,
	FieldSectionHeader
} from "@adapptio/lib-editor-ui";
import { InspectorNode } from "../InspectorNode/InspectorNode";
import { isNonEmptyArray } from "@adapptio/lib-shared";
import { InspectorNodeLabel } from "../InspectorNodeLabel";

export interface IInspectorNodeDefaultProps {
	node: IInspectorNode_Default;
	depth?: number;
}

export const InspectorNodeDefault: React.FC<IInspectorNodeDefaultProps> = ({ node, depth, children }) => {
	const t = useTranslate();

	const label = t("editor", node.label);

	const controls = isNonEmptyArray(node.controls)
		? node.controls?.map((control) => (
			<InspectorControl key={`${node.key}-${control.type}`} control={control} />
		))
		: null;

	const collapsedControls = isNonEmptyArray(node.collapsedControls)
		? node.collapsedControls?.map((control) => (
			<InspectorControl key={`${node.key}-${control.type}`} control={control} />
		))
		: null;

	const childDepth = label || controls ? depth + 1 : depth;

	const childInspectors = React.Children.toArray(children).reduce<React.ReactNode[]>((acc, child) => {
		if (React.isValidElement(child)) {
			return [ ...acc, React.cloneElement(child, { depth: childDepth } as React.HTMLAttributes<unknown>) ];
		}

		return acc;
	}, []);

	const resolvedChildren = node.children?.reduce<React.ReactNode[]>((acc, resolveChildNode, index) => {
		const childNode = resolveChildNode();

		if (!childNode) {
			return acc;
		}

		return [
			...acc,
			<InspectorNode key={`${childNode.key}-${index}`} getNode={resolveChildNode} depth={childDepth} />
		];
	}, []);

	const allChildren = [ ...childInspectors, ...resolvedChildren ];

	const childs = isNonEmptyArray(allChildren) ? allChildren : null;

	if (childs) {
		if (label || controls) {
			return (
				<FieldSection
					key={node.key}
					stateKey={node.key}
					collapsible
					defaultCollapsed={node.defaultCollapsed}
					group={node.group}
				>
					<FieldSectionHeader
						key={`${node.key}-${label}`}
						controls={controls}
						collapsedControls={collapsedControls}
					>
						{label && !node?.group && <InspectorNodeLabel node={node} />}
						{label && node.group && <FieldLabel>{label}</FieldLabel>}
					</FieldSectionHeader>
					<FieldSectionContent disablePadding={(depth === 0 && node.group) || depth === 1}>
						{childs}
					</FieldSectionContent>
				</FieldSection>
			);
		}

		return <>{childs}</>;
	}

	if (label || controls) {
		return (
			<FieldRow>
				{label && <InspectorNodeLabel node={node} />}
				{controls}
			</FieldRow>
		);
	}

	return null;
};

InspectorNodeDefault.defaultProps = {
	depth: 0
};
